import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { BehaviorSubject, Observable, catchError, map, throwError } from 'rxjs';
import {
  IWHShiftGroupDTO,
  IWHShiftModelDTO,
  PageResultShiftGroupDto,
  WHShiftGroupDataService,
  FLOW_GATEWAY,
  NumberValidator,
  noWhitespaceValidator,
} from '@workheld/workheld-shared-lib';
import { environment } from '../../environments/environment';

export interface SelectTime {
  time: string;
}

class ShiftGroupEntity {
  _pageResult: BehaviorSubject<PageResultShiftGroupDto>;
  pageResult$: Observable<PageResultShiftGroupDto>;
  _loading: BehaviorSubject<boolean>;
  loading$: Observable<boolean>;
  constructor() {
    this._pageResult = new BehaviorSubject<PageResultShiftGroupDto>(null);
    this.pageResult$ = this._pageResult.asObservable();
    this._loading = new BehaviorSubject<boolean>(false);
    this.loading$ = this._loading.asObservable();
  }
}

@Injectable({
  providedIn: 'root',
})
export class ShiftGroupDataService {
  public data = new ShiftGroupEntity();

  constructor(private shiftGroupDataService: WHShiftGroupDataService) {}

  public initParamsFormGroup() {
    return new FormBuilder().group({
      page: [0],
      status: [''],
      term: [null],
    });
  }

  getShiftGroups(params?: any) {
    this.data._loading.next(true);
    this.shiftGroupDataService
      .getShiftGroups({ apiUrl: environment.apiUrl + FLOW_GATEWAY }, params)
      .pipe(
        map((data: PageResultShiftGroupDto) => {
          this.data._pageResult.next(data);
          this.data._loading.next(false);
        }),
        catchError((err) => {
          this.data._loading.next(false);
          return throwError(() => err);
        }),
      )
      .subscribe();
  }

  getShiftGroup(id: string) {
    const config = {
      apiUrl: environment.apiUrl + FLOW_GATEWAY,
      id,
    };
    return this.shiftGroupDataService.getShiftGroupById(config);
  }

  createOrUpdateShiftGroup(shiftGroup: IWHShiftGroupDTO) {
    this.data._loading.next(true);
    return this.shiftGroupDataService.addShiftGroup(
      { apiUrl: environment.apiUrl + FLOW_GATEWAY, id: shiftGroup.extId },
      shiftGroup,
    );
  }

  addShiftModels(shiftGroup: IWHShiftGroupDTO) {
    this.data._loading.next(true);
    return this.shiftGroupDataService.updateShiftModels(
      {
        apiUrl: environment.apiUrl + FLOW_GATEWAY,
        shiftgroupId: shiftGroup.id,
      },
      shiftGroup.shiftModelsList,
    );
  }

  /**
   *
   * @param shiftGroupId
   * @param shiftModel
   * @returns
   */
  updateShiftModel(shiftGroupId: string, shiftModel: IWHShiftModelDTO) {
    this.data._loading.next(true);

    return this.shiftGroupDataService.updateShiftModel(
      { apiUrl: environment.apiUrl + FLOW_GATEWAY, shiftgroupId: shiftGroupId },
      shiftModel,
    );
  }

  deleteShiftGroup(shiftGroup: IWHShiftGroupDTO) {
    this.data._loading.next(true);

    return this.shiftGroupDataService.deleteShiftGroup({
      apiUrl: environment.apiUrl + FLOW_GATEWAY,
      id: shiftGroup.id,
    });
  }

  deleteShiftModel(shiftModel: IWHShiftModelDTO) {
    this.data._loading.next(true);
    return this.shiftGroupDataService.deleteShiftModel({
      apiUrl: environment.apiUrl + FLOW_GATEWAY,
      shiftgroupId: shiftModel.shiftModelGroupId,
      id: shiftModel.id,
    });
  }

  getUnassignedShiftModels(teamId: string, date: string) {
    this.data._loading.next(true);
    return this.shiftGroupDataService.getUnassignedShiftModels(
      { apiUrl: environment.apiUrl + FLOW_GATEWAY },
      { teamId, date },
    );
  }
}

export function createShiftModelFormGroup(): FormGroup {
  return new FormGroup({
    name: new FormControl('', [Validators.required, noWhitespaceValidator]),
    startTime: new FormControl('', [
      Validators.required,
      Validators.pattern(/^([0-1]?\d|2[0-3]):[0-5]\d$/),
    ]),
    endTime: new FormControl('', [
      Validators.required,
      Validators.pattern(/^([0-1]?\d|2[0-3]):[0-5]\d$/),
    ]),
    status: new FormControl('ACTIVE'),
    breakDuration: new FormControl(0, [
      Validators.required,
      NumberValidator.number({ min: 0, max: 1440 }),
    ]),
    shiftModelGroupId: new FormControl(''),
  });
}

export function updateShiftModelFormGroup(
  shiftModel: IWHShiftModelDTO,
): FormGroup {
  return new FormGroup({
    id: new FormControl(shiftModel.id),
    extId: new FormControl(shiftModel.extId),
    name: new FormControl(shiftModel.name, [
      Validators.required,
      noWhitespaceValidator,
    ]),
    startTime: new FormControl(shiftModel.startTime, [
      Validators.required,
      Validators.pattern(/^([0-1]?\d|2[0-3]):[0-5]\d$/),
    ]),
    endTime: new FormControl(shiftModel.endTime, [
      Validators.required,
      Validators.pattern(/^([0-1]?\d|2[0-3]):[0-5]\d$/),
    ]),
    status: new FormControl(shiftModel.status),
    breakDuration: new FormControl(shiftModel.breakDuration, [
      Validators.required,
      NumberValidator.number({ min: 0, max: 1440 }),
    ]),
    shiftModelGroupId: new FormControl(shiftModel.shiftModelGroupId),
  });
}

export function startAndEndTimeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.parent) {
      return null; // Control is not yet associated with a parent.
    }

    let startTime = control.parent.get('startTime').value;
    let endTime = control.parent.get('endTime').value;

    //If shiftModel.StartTime is an object then convert Object.time to String
    if (typeof startTime === 'object') {
      const tempStartTime = structuredClone(startTime as SelectTime);
      startTime = tempStartTime ? tempStartTime.time : '';
    }

    //If shiftModel.StartTime is an object then convert Object.time to String
    if (typeof endTime === 'object') {
      const tempEndTime = structuredClone(endTime as SelectTime);
      endTime = tempEndTime ? tempEndTime.time : '';
    }

    if (startTime === '' || endTime === '') {
      return null; // don't validate empty value
    }

    // time in format hh:mm:ss get the hh and mm and ss in three numbers objects
    const startTimeHours = startTime.split(':').map((x) => parseInt(x));
    const endTimeHours = endTime.split(':').map((x) => parseInt(x));

    // WRite regex to validate
    //check if the start time is before the end time and save in a boolean
    const startTimeBeforeEndTime =
      startTimeHours[0] < endTimeHours[0] ||
      (startTimeHours[0] === endTimeHours[0] &&
        startTimeHours[1] < endTimeHours[1]);

    return !startTimeBeforeEndTime ? { startBeforeEnd: true } : null;
  };
}

const MIN_SHIFT_DURATION_MINUTES = 60;
const MAX_SHIFT_DURATION_MINUTES = 24 * 60;

function parseTimeToMinutes(time: string): number | null {
  if (!time) return null;
  const [hours, minutes] = time.split(':').map((x) => parseInt(x, 10));
  return hours * 60 + minutes;
}

/**
 * Calculates shift duration in minutes, handling overnight shifts.
 * E.g., 22:00 to 06:00 = 8 hours (480 minutes)
 * E.g., 06:00 to 05:59 = 23h 59m (1439 minutes) - max allowed
 */
function calculateShiftDuration(
  startMinutes: number,
  endMinutes: number,
): number {
  if (endMinutes > startMinutes) {
    // Same day shift (e.g., 08:00 to 16:00)
    return endMinutes - startMinutes;
  } else {
    // Overnight shift (e.g., 22:00 to 06:00)
    return MAX_SHIFT_DURATION_MINUTES - startMinutes + endMinutes;
  }
}

/**
 * Checks if a shift duration is valid (between 1 hour and 24 hours)
 */
function isValidShiftDuration(startTime: string, endTime: string): boolean {
  const startMinutes = parseTimeToMinutes(startTime);
  const endMinutes = parseTimeToMinutes(endTime);

  if (startMinutes === null || endMinutes === null) return true; // Skip validation if times are missing

  const duration = calculateShiftDuration(startMinutes, endMinutes);
  return duration >= MIN_SHIFT_DURATION_MINUTES;
}

/**
 * Checks if two shifts overlap, handling overnight shifts correctly.
 * Converts shifts to minute ranges and checks for intersection.
 */
function doShiftsOverlap(
  shift1Start: string,
  shift1End: string,
  shift2Start: string,
  shift2End: string,
): boolean {
  const s1Start = parseTimeToMinutes(shift1Start);
  const s1End = parseTimeToMinutes(shift1End);
  const s2Start = parseTimeToMinutes(shift2Start);
  const s2End = parseTimeToMinutes(shift2End);

  if (
    s1Start === null ||
    s1End === null ||
    s2Start === null ||
    s2End === null
  ) {
    return false;
  }

  // Convert to ranges considering overnight shifts
  const shift1Ranges = getShiftRanges(s1Start, s1End);
  const shift2Ranges = getShiftRanges(s2Start, s2End);

  // Check if any ranges overlap
  return shift1Ranges.some((r1) =>
    shift2Ranges.some((r2) => rangesOverlap(r1, r2)),
  );
}

/**
 * Returns time ranges for a shift. Overnight shifts are split into two ranges.
 * E.g., 22:00-06:00 becomes [[22:00-24:00], [00:00-06:00]]
 */
function getShiftRanges(
  startMinutes: number,
  endMinutes: number,
): [number, number][] {
  if (endMinutes > startMinutes) {
    // Same day shift
    return [[startMinutes, endMinutes]];
  } else {
    // Overnight shift - split into two ranges
    return [
      [startMinutes, MAX_SHIFT_DURATION_MINUTES],
      [0, endMinutes],
    ];
  }
}

/**
 * Checks if two minute ranges overlap
 */
function rangesOverlap(
  range1: [number, number],
  range2: [number, number],
): boolean {
  return range1[0] < range2[1] && range2[0] < range1[1];
}

// ==========================================
// Error Messages
// ==========================================

export const SHIFT_VALIDATION_ERRORS = {
  DURATION_TOO_SHORT: 'shiftDurationTooShort',
  SHIFTS_OVERLAP: 'shiftsOverlap',
  DUPLICATE_NAME: 'duplicateName',
} as const;

export interface ShiftDurationError {
  minDurationMinutes: number;
  actualDurationMinutes: number;
  shiftIndex?: number;
  startTime: string;
  endTime: string;
}

export interface ShiftsOverlapError {
  shift1Index: number;
  shift2Index: number;
  shift1Name?: string;
  shift2Name?: string;
  shift1Times: { start: string; end: string };
  shift2Times: { start: string; end: string };
}

export interface DuplicateNameError {
  duplicateNames: string[];
}

export function validateOverlappingTimesArrayGroup(): ValidatorFn {
  return (formArray: FormArray): ValidationErrors | null => {
    // getRawValue keeps disabled (read-only) shifts in the validation
    const shifts = formArray.getRawValue();
    const errors: ValidationErrors = {};

    for (let i = 0; i < shifts.length; i++) {
      const currentShift = shifts[i];
      const startMinutes = parseTimeToMinutes(currentShift.startTime);
      const endMinutes = parseTimeToMinutes(currentShift.endTime);

      // Validate minimum duration
      if (
        startMinutes !== null &&
        endMinutes !== null &&
        !isValidShiftDuration(currentShift.startTime, currentShift.endTime)
      ) {
        const duration = calculateShiftDuration(startMinutes, endMinutes);
        errors[SHIFT_VALIDATION_ERRORS.DURATION_TOO_SHORT] = {
          minDurationMinutes: MIN_SHIFT_DURATION_MINUTES,
          actualDurationMinutes: duration,
          shiftIndex: i,
          startTime: currentShift.startTime,
          endTime: currentShift.endTime,
        } as ShiftDurationError;
      }

      // Check for overlaps with other shifts
      for (let j = i + 1; j < shifts.length; j++) {
        const otherShift = shifts[j];

        if (
          doShiftsOverlap(
            currentShift.startTime,
            currentShift.endTime,
            otherShift.startTime,
            otherShift.endTime,
          )
        ) {
          errors[SHIFT_VALIDATION_ERRORS.SHIFTS_OVERLAP] = {
            shift1Index: i,
            shift2Index: j,
            shift1Name: currentShift.name || undefined,
            shift2Name: otherShift.name || undefined,
            shift1Times: {
              start: currentShift.startTime,
              end: currentShift.endTime,
            },
            shift2Times: {
              start: otherShift.startTime,
              end: otherShift.endTime,
            },
          } as ShiftsOverlapError;
          break;
        }
      }

      if (errors[SHIFT_VALIDATION_ERRORS.SHIFTS_OVERLAP]) break;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };
}

/**
 * Validates a single shift FormGroup:
 * - Shift must be at least 1 hour long
 */
export function validateStartAndEndDate(): ValidatorFn {
  return (formGroup: FormGroup): ValidationErrors | null => {
    const startTime = formGroup.controls['startTime']?.value;
    const endTime = formGroup.controls['endTime']?.value;

    if (!startTime || !endTime) return null;

    const startMinutes = parseTimeToMinutes(startTime);
    const endMinutes = parseTimeToMinutes(endTime);

    if (startMinutes === null || endMinutes === null) return null;

    if (!isValidShiftDuration(startTime, endTime)) {
      const duration = calculateShiftDuration(startMinutes, endMinutes);
      return {
        [SHIFT_VALIDATION_ERRORS.DURATION_TOO_SHORT]: {
          minDurationMinutes: MIN_SHIFT_DURATION_MINUTES,
          actualDurationMinutes: duration,
          startTime,
          endTime,
        } as ShiftDurationError,
      };
    }

    return null;
  };
}

export function validateNamesArrayGroup(): ValidatorFn {
  return (formArray: FormArray): ValidationErrors | null => {
    const tempList = structuredClone(
      formArray.getRawValue(),
    ) as IWHShiftModelDTO[];

    if (tempList.length < 2) {
      return null;
    }

    // Find all duplicate names
    const nameCounts = new Map<string, number>();
    tempList.forEach((item) => {
      if (item.name && item.name.trim() !== '') {
        const name = item.name.trim();
        nameCounts.set(name, (nameCounts.get(name) || 0) + 1);
      }
    });

    const duplicateNames = Array.from(nameCounts.entries())
      .filter(([_, count]) => count > 1)
      .map(([name]) => name);

    if (duplicateNames.length > 0) {
      return {
        [SHIFT_VALIDATION_ERRORS.DUPLICATE_NAME]: {
          duplicateNames,
        } as DuplicateNameError,
      };
    }

    return null;
  };
}
